<template>
    <PageSidebar>
        <template #sidebarTitle> {{ $t("pricing.menu.header") }} </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <div class="space-y-2">
                    <FormInput
                        v-if="ecPricing?.title"
                        :title="$t('shared.title')"
                        disabled
                        v-model="ecPricing.title"
                        :help="$t('pricing.ec_info')"
                    />

                    <FormRichText
                        id="pricingTitle"
                        :title="ecPricing?.title ? '' : $t('shared.title')"
                        :placeholder="$t('shared.title')"
                        v-model="pricing.title"
                        :rows="2"
                    />
                </div>
                <div class="space-y-2">
                    <FormTextArea
                        v-if="ecPricing?.subtitle"
                        :title="$t('shared.subtitle')"
                        disabled
                        v-model="ecPricing.subtitle"
                        :help="$t('pricing.ec_info')"
                    />
                    <FormRichText
                        id="pricingSubtitle"
                        :title="ecPricing?.subtitle ? '' : $t('shared.subtitle')"
                        v-model="pricing.subtitle"
                        :rows="5"
                    />
                </div>

                <div class="space-y-2">
                    <FormLabel v-if="ecPricing?.img" :truncate="true">
                        {{ $t("pricing.header.pricing_img") }}
                    </FormLabel>
                    <CardFileHorizontal :file="ecFile" v-if="ecFile.url" />
                    <p class="ct-text" v-if="ecPricing?.img">
                        {{ $t("pricing.ec_info") }}
                    </p>
                    <FormFiles
                        :title="ecPricing?.img ? '' : $t('pricing.header.pricing_img')"
                        accept="image/*"
                        :labels="{
                            clickToUpload: $t('shared.uploader.click_to_upload'),
                            dragAndDrop: $t('shared.uploader.drag_and_drop'),
                            fileInfo: $t('shared.uploader.file_details'),
                        }"
                        :maxSize="10000"
                        :errorMessages="{
                            invalidSize: $t('shared.invalid.file_size_over'),
                            invalidType: $t('shared.invalid.file_type'),
                        }"
                        :single-file="true"
                        v-model="files"
                        @fileSelected="uploadImg"
                        @fileRemoved="removeImg"
                    />
                </div>

                <div class="space-y-2">
                    <FormLabel>
                        {{ $t("pricing.header.header_layout") }}
                    </FormLabel>
                    <FormSwitch :options="headerLayouts" v-model="pricing.headerLayout" />
                </div>

                <template v-if="pricing.headerLayout === PRICING_HEADER_LAYOUTS.standard">
                    <div class="space-y-2">
                        <FormLabel>
                            {{ $t("pricing.header.img_position") }}
                        </FormLabel>
                        <FormSwitch :options="imgPositions" v-model="pricing.imgPosition" />
                    </div>

                    <FormInput
                        :title="$t('pricing.header.img_height')"
                        :unit="'px'"
                        :validate="'isNumber'"
                        v-model="pricing.imgSize"
                    />
                    <FormInput
                        :title="$t('pricing.header.img_radius')"
                        :unit="'px'"
                        :validate="'isNumber'"
                        v-model="pricing.imgRadius"
                    />
                </template>
                <template v-else>
                    <FormFiles
                        :title="$t('pricing.header.pricing_background')"
                        accept="image/*"
                        :labels="{
                            clickToUpload: $t('shared.uploader.click_to_upload'),
                            dragAndDrop: $t('shared.uploader.drag_and_drop'),
                            fileInfo: $t('shared.uploader.file_details'),
                        }"
                        :maxSize="10000"
                        :errorMessages="{
                            invalidSize: $t('shared.invalid.file_size_over'),
                            invalidType: $t('shared.invalid.file_type'),
                        }"
                        :single-file="true"
                        v-model="files2"
                        @fileSelected="uploadBackground"
                        @fileRemoved="removeBackground"
                    />
                </template>
            </div>
            <CardToggle v-if="pricing.socials" :title="$t('faq.layout.socials')">
                <SocialsList @edit="$emit('editSocial', $event)" />
            </CardToggle>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="saveSnapshot" :disabled="!hasChanges" />
        </template>
    </PageSidebar>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import {
    FormFiles,
    FormInput,
    FormLabel,
    FormRichText,
    FormSwitch,
    FormTextArea,
    makeFileModel,
} from "@easy/ui/forms";
import { CardFileHorizontal, CardToggle } from "@easy/ui/cards";
import SocialsList from "./SocialsList.vue";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { PRICING_HEADER_LAYOUTS } from "@/apps/pricing/services/pricing.service";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    name: "HeaderMain",
    components: {
        PageSidebar,
        FormInput,
        FormTextArea,
        CardToggle,
        SocialsList,
        FormSwitch,
        FormLabel,
        FormFiles,
        CardFileHorizontal,
        cSaveButton,
        FormRichText,
    },
    setup() {
        useFocusOnQueryField();
    },
    data() {
        return {
            PRICING_HEADER_LAYOUTS,
            files: [],
            files2: [],
            ecFile: {},
        };
    },
    computed: {
        ...mapState("pricing", ["pricing", "ecPricing", "hasChanges"]),
        imgPositions() {
            return [
                { value: "top", label: this.$t("pricing.header.top") },
                { value: "side", label: this.$t("pricing.header.side") },
            ];
        },
        headerLayouts() {
            return [
                {
                    value: PRICING_HEADER_LAYOUTS.standard,
                    label: this.$t("pricing.header.standard"),
                },
                {
                    value: PRICING_HEADER_LAYOUTS.background,
                    label: this.$t("pricing.header.background"),
                },
            ];
        },
    },
    watch: {
        pricing: {
            handler(pricing, oldPricing) {
                if (!oldPricing?.img && pricing?.img) {
                    this.files = [
                        makeFileModel({
                            url: pricing.img,
                        }),
                    ];
                    this.files2 = [
                        makeFileModel({
                            url: pricing.backImg,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
        ecPricing: {
            handler(ecPricing, oldEcPricing) {
                if (!oldEcPricing?.img && ecPricing?.img) {
                    this.ecFile = makeFileModel({
                        url: ecPricing.img,
                        canRemove: false,
                    });
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        ...mapActions("pricing/projects", ["saveSnapshot"]),
        ...mapActions("pricing", ["uploadPricingImg", "uploadPricingBackImg"]),
        removeImg() {
            this.pricing.img = "";
        },
        async uploadImg(file) {
            this.pricing.img = await this.uploadPricingImg({ file });
        },
        removeBackground() {
            this.pricing.backImg = "";
        },
        async uploadBackground(file) {
            this.pricing.backImg = await this.uploadPricingBackImg({ file });
        },
    },
};
</script>
